import { useMemo } from "react";
import { MOOD_COLORS } from "../../../constants/moodColors";
import type { BalancePieDataItem } from "../../../features/trends/hooks/useTrendsData";
import type { MoodPercentages } from "../../../features/trends/hooks/useMoodPercentages";

/** Build pie data showing top N moods by count (for weekly overview) */
export function getTopMoodsPieData(
  moodCounts: Record<string, number>,
  totalMoods: number,
  topN: number = 2
): BalancePieDataItem[] {
  const entries = Object.entries(moodCounts)
    .filter(([, count]) => count > 0)
    .sort(([, a], [, b]) => b - a)
    .slice(0, topN);

  return entries.map(([name, value]) => ({
    name,
    value,
    percentage:
      totalMoods > 0 ? Math.round((value / totalMoods) * 100) + "%" : "0%",
    label: `${name} (${value} units)`,
    color: (MOOD_COLORS as Record<string, string>)[name] || "#D3D3D3",
  }));
}

export interface UsePieChartDataReturn {
  balancePieData: BalancePieDataItem[];
  modifiedBalancePieData: BalancePieDataItem[];
}

export function usePieChartData(
  moodCounts: Record<string, number>,
  totalMoods: number,
  moodPercentages?: MoodPercentages
): UsePieChartDataReturn {
  const balancePieData = useMemo(() => {
    const apathyCount = Number(moodCounts["Apathy"]) || 0;
    const flowCount = Number(moodCounts["Flow"]) || 0;
    const anxietyCount = Number(moodCounts["Anxiety"]) || 0;
    const confidentCount = Number(moodCounts["Confident"]) || 0;
    const anxietyApathyCount = apathyCount + anxietyCount;

    // Use moodPercentages if available for consistent percentages across tabs
    let flowPercentage: string;
    let confidentPercentage: string;
    let anxietyApathyPercentage: string;

    if (moodPercentages) {
      const anxietyPct = moodPercentages.Anxiety || 0;
      const apathyPct = moodPercentages.Apathy || 0;
      anxietyApathyPercentage = anxietyPct + apathyPct + "%";
      flowPercentage = moodPercentages.Flow + "%";
      confidentPercentage = moodPercentages.Confident + "%";
    } else {
      // Fallback to count-based calculation
      flowPercentage =
        totalMoods > 0
          ? Math.round((flowCount / totalMoods) * 100) + "%"
          : "0%";
      confidentPercentage =
        totalMoods > 0
          ? Math.round((confidentCount / totalMoods) * 100) + "%"
          : "0%";
      anxietyApathyPercentage =
        totalMoods > 0
          ? Math.round((anxietyApathyCount / totalMoods) * 100) + "%"
          : "0%";
    }

    return [
      {
        name: "AnxietyApathy",
        value: anxietyApathyCount,
        percentage: anxietyApathyPercentage,
        label: `Other (${anxietyApathyCount} units)`,
        color: MOOD_COLORS.AnxietyApathy,
      },
      {
        name: "Flow",
        value: flowCount,
        percentage: flowPercentage,
        label: `Flow (${flowCount} units)`,
        color: MOOD_COLORS.Flow,
      },
      {
        name: "Confident",
        value: confidentCount,
        percentage: confidentPercentage,
        label: `Confident (${confidentCount} units)`,
        color: MOOD_COLORS.Confident,
      },
    ];
  }, [moodCounts, totalMoods, moodPercentages]);

  const modifiedBalancePieData = useMemo(() => {
    const flowEntry = balancePieData.find((entry) => entry.name === "Flow");
    const confidentEntry = balancePieData.find(
      (entry) => entry.name === "Confident"
    );

    const flowValue = flowEntry?.value || 0;
    const confidentValue = confidentEntry?.value || 0;
    const flowConfidentCount = flowValue + confidentValue;

    const anxietyCount = Number(moodCounts["Anxiety"]) || 0;
    const apathyCount = Number(moodCounts["Apathy"]) || 0;

    // Use moodPercentages if available for consistent percentages across tabs
    let flowConfidentPercentage: string;
    let anxietyPercentage: string;
    let apathyPercentage: string;

    if (moodPercentages) {
      const flowPct = moodPercentages.Flow || 0;
      const confidentPct = moodPercentages.Confident || 0;
      flowConfidentPercentage = flowPct + confidentPct + "%";
      anxietyPercentage = moodPercentages.Anxiety + "%";
      apathyPercentage = moodPercentages.Apathy + "%";
    } else {
      // Fallback to count-based calculation
      flowConfidentPercentage =
        totalMoods > 0
          ? Math.round((flowConfidentCount / totalMoods) * 100) + "%"
          : "0%";
      anxietyPercentage =
        totalMoods > 0
          ? Math.round((anxietyCount / totalMoods) * 100) + "%"
          : "0%";
      apathyPercentage =
        totalMoods > 0
          ? Math.round((apathyCount / totalMoods) * 100) + "%"
          : "0%";
    }

    return [
      {
        name: "FlowConfident",
        value: flowConfidentCount,
        percentage: flowConfidentPercentage,
        label: `Other (${flowConfidentCount} units)`,
        color: MOOD_COLORS.FlowConfident,
      },
      ...(anxietyCount > 0
        ? [
            {
              name: "Anxiety",
              value: anxietyCount,
              percentage: anxietyPercentage,
              label: `Anxiety (${anxietyCount} units)`,
              color: MOOD_COLORS.Anxiety,
            },
          ]
        : []),
      ...(apathyCount > 0
        ? [
            {
              name: "Apathy",
              value: apathyCount,
              percentage: apathyPercentage,
              label: `Apathy (${apathyCount} units)`,
              color: MOOD_COLORS.Apathy,
            },
          ]
        : []),
    ];
  }, [balancePieData, moodCounts, totalMoods, moodPercentages]);

  console.log("moodCounts 1 -> " + JSON.stringify(moodCounts));
  console.log("moodPercentages -> " + JSON.stringify(moodPercentages));
  return {
    balancePieData,
    modifiedBalancePieData,
  };
}
